(() => {
  if (window.CartQuantitySyncInitialized) return;
  window.CartQuantitySyncInitialized = true;

  const CART_MUTATION_PATTERN =
    /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;

  let cachedCart = null;
  let syncTimer = null;
  let requestInProgress = false;
  let productQuantityRequest = null;

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function getVariantQuantity(cart, variantId) {
    if (!cart?.items || !variantId) return 0;

    return cart.items.reduce((total, item) => {
      if (String(item.variant_id) === String(variantId)) {
        return total + Number(item.quantity || 0);
      }

      return total;
    }, 0);
  }

  function updateQuantityInputs(cart, root = document) {
    if (!cart?.items) return;

    root
      .querySelectorAll('[data-product-quantity-sync]')
      .forEach((input) => {
        const selectedVariantId = input.form
          ?.querySelector('input[name="id"]')
          ?.value;
        const variantId = selectedVariantId || input.dataset.quantityVariantId;

        if (!variantId) return;

        // Keep the explicit sync key aligned with the product form. The form
        // input is updated immediately during option selection and is the
        // authoritative selected variant.
        input.dataset.quantityVariantId = String(variantId);

        const cartQuantity = getVariantQuantity(cart, variantId);
        const minimum = Number(
          input.dataset.min || input.getAttribute('min') || 1
        );
        const maximum = Number(
          input.dataset.max || input.getAttribute('max') || Infinity
        );

        /*
         * When the variant isn't in the cart, keep the product-page
         * quantity at its minimum value, normally 1.
         */
        let displayedQuantity =
          cartQuantity > 0 ? cartQuantity : Math.max(minimum, 1);

        if (Number.isFinite(maximum)) {
          displayedQuantity = Math.min(displayedQuantity, maximum);
        }

        // A product already in the cart can be reduced to zero from the PDP.
        // When it is not in the cart, keep the normal add-to-cart minimum.
        input.min = String(cartQuantity > 0 ? 0 : Math.max(minimum, 1));
        input.value = displayedQuantity;
        input.setAttribute('value', String(displayedQuantity));
        input.dataset.cartQuantity = String(cartQuantity);

        input.dispatchEvent(
          new CustomEvent('cart-quantity:synchronized', {
            bubbles: true,
            detail: {
              variantId,
              cartQuantity,
              displayedQuantity,
            },
          })
        );
      });
  }

  function getSectionsToRender() {
    const drawer = document.querySelector('cart-drawer');
    const drawerSections = drawer?.getSectionsToRender?.() || [];
    const sections = drawerSections.map((section) => section.section);

    return [...new Set([...sections, 'cart-drawer', 'cart-icon-bubble'])];
  }

  function renderCartSections(cart) {
    if (!cart?.sections) return;

    const parser = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    const drawerHTML = cart.sections['cart-drawer'];

    if (drawer && drawerHTML) {
      const freshDrawer = parser
        .parseFromString(drawerHTML, 'text/html')
        .querySelector('cart-drawer');

      if (freshDrawer) {
        drawer.innerHTML = freshDrawer.innerHTML;
        const isEmpty = Number.isFinite(cart.item_count)
          ? cart.item_count === 0
          : freshDrawer.classList.contains('is-empty');
        drawer.classList.toggle('is-empty', isEmpty);
      }
    }

    const bubbleHTML = cart.sections['cart-icon-bubble'];
    if (bubbleHTML) {
      const currentBubble = document.getElementById('cart-icon-bubble');
      const bubbleDocument = parser.parseFromString(bubbleHTML, 'text/html');
      const freshBubble =
        bubbleDocument.getElementById('cart-icon-bubble') ||
        bubbleDocument.querySelector('.shopify-section');

      if (currentBubble && freshBubble) {
        currentBubble.innerHTML = freshBubble.innerHTML;
      }
    }
  }

  function setProductQuantityBusy(input, busy) {
    const quantityInput = input.closest('quantity-input');
    quantityInput?.toggleAttribute('aria-busy', busy);
    quantityInput
      ?.querySelectorAll('button')
      .forEach((button) => (button.disabled = busy));
  }

  async function setCartVariantQuantity(input) {
    const variantId =
      input.form?.querySelector('input[name="id"]')?.value ||
      input.dataset.quantityVariantId;
    const desiredQuantity = Math.max(0, Number.parseInt(input.value, 10) || 0);

    if (!variantId) return;

    if (productQuantityRequest) {
      productQuantityRequest.abort();
    }

    const controller = new AbortController();
    productQuantityRequest = controller;
    setProductQuantityBusy(input, true);

    try {
      cachedCart = null;
      const currentCart = await getCart();
      if (!currentCart || controller.signal.aborted) return;

      const matchingLines = currentCart.items.filter(
        (item) => String(item.variant_id) === String(variantId)
      );
      const currentQuantity = matchingLines.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      );

      if (currentQuantity === desiredQuantity) {
        updateQuantityInputs(currentCart);
        return;
      }

      const sections = getSectionsToRender();
      let url;
      let body;

      if (matchingLines.length === 0) {
        if (desiredQuantity === 0) return;

        url = `${getShopifyRoot()}cart/add.js`;
        body = {
          items: [{ id: Number(variantId), quantity: desiredQuantity }],
          sections,
          sections_url: window.location.pathname,
        };
      } else {
        const updates = {};
        const difference = desiredQuantity - currentQuantity;

        if (difference > 0) {
          const line = matchingLines[0];
          updates[line.key] = Number(line.quantity || 0) + difference;
        } else {
          let quantityToRemove = Math.abs(difference);

          // Reduce the newest matching lines first so distinct line-item
          // properties on the original line are preserved when possible.
          [...matchingLines].reverse().forEach((line) => {
            if (quantityToRemove === 0) return;
            const lineQuantity = Number(line.quantity || 0);
            const removed = Math.min(lineQuantity, quantityToRemove);
            updates[line.key] = lineQuantity - removed;
            quantityToRemove -= removed;
          });
        }

        url = `${getShopifyRoot()}cart/update.js`;
        body = {
          updates,
          sections,
          sections_url: window.location.pathname,
        };
      }

      const response = await originalFetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Unable to update cart quantity: ${response.status}`);
      }

      const mutationState = await response.json();
      renderCartSections(mutationState);

      // cart/add.js returns the added lines rather than the complete cart.
      // Always reconcile from cart.js so every PDP selector uses the real total.
      cachedCart = null;
      const updatedCart = await getCart();
      if (!updatedCart || controller.signal.aborted) return;
      updateQuantityInputs(updatedCart);

      document.dispatchEvent(
        new CustomEvent('cart-quantity:updated', {
          detail: { cart: updatedCart, variantId: String(variantId) },
        })
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Cart quantity sync]', error);
        cachedCart = null;
        await synchronizeCartQuantities();
      }
    } finally {
      if (productQuantityRequest === controller) {
        productQuantityRequest = null;
        setProductQuantityBusy(input, false);
      }
    }
  }

  async function getCart() {
    if (requestInProgress) return cachedCart;

    requestInProgress = true;

    try {
      const response = await fetch(`${getShopifyRoot()}cart.js`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`Unable to load cart: ${response.status}`);
      }

      cachedCart = await response.json();
      return cachedCart;
    } catch (error) {
      console.error('[Cart quantity sync]', error);
      return null;
    } finally {
      requestInProgress = false;
    }
  }

  async function synchronizeCartQuantities() {
    const cart = await getCart();

    if (!cart) return;

    updateQuantityInputs(cart);

    document.dispatchEvent(
      new CustomEvent('cart-quantity:updated', {
        detail: { cart },
      })
    );
  }

  function scheduleSynchronization(delay = 50) {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      synchronizeCartQuantities();
    }, delay);
  }

  /*
   * Detect cart changes made with fetch(), including:
   * cart/add.js
   * cart/change.js
   * cart/update.js
   * cart/clear.js
   */
  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    try {
      const request = args[0];
      const url =
        typeof request === 'string'
          ? request
          : request instanceof Request
            ? request.url
            : '';

      if (response.ok && CART_MUTATION_PATTERN.test(url)) {
        cachedCart = null;

        /*
         * The small delay lets the theme finish replacing the
         * cart-drawer HTML before quantity inputs are synchronized.
         */
        scheduleSynchronization(100);
      }
    } catch (error) {
      console.error('[Cart quantity sync]', error);
    }

    return response;
  };

  /*
   * Detect themes that use XMLHttpRequest instead of fetch().
   */
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this._cartQuantitySyncUrl = String(url || '');

    return originalXHROpen.call(this, method, url, ...args);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (CART_MUTATION_PATTERN.test(this._cartQuantitySyncUrl || '')) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) {
          cachedCart = null;
          scheduleSynchronization(100);
        }
      });
    }

    return originalXHRSend.apply(this, args);
  };

  /*
   * When the theme replaces the cart drawer, update any newly
   * inserted quantity inputs using the last fetched cart.
   */
  const observer = new MutationObserver((mutations) => {
    if (!cachedCart) return;

    let quantityInputAdded = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (
          node.matches?.('[data-product-quantity-sync]') ||
          node.querySelector?.('[data-product-quantity-sync]')
        ) {
          quantityInputAdded = true;
          break;
        }
      }

      if (quantityInputAdded) break;
    }

    if (quantityInputAdded) {
      updateQuantityInputs(cachedCart);
    }
  });

  function initialize() {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    synchronizeCartQuantities();
  }

  /*
   * Support custom cart events that may already be used by the theme.
   */
  document.addEventListener('cart:updated', () => {
    cachedCart = null;
    scheduleSynchronization();
  });

  document.addEventListener('cart:refresh', () => {
    cachedCart = null;
    scheduleSynchronization();
  });

  document.addEventListener('cartUpdate', () => {
    cachedCart = null;
    scheduleSynchronization();
  });

  document.addEventListener('product:added', () => {
    cachedCart = null;
    scheduleSynchronization();
  });

  /*
   * This event can be dispatched manually from other theme scripts:
   *
   * document.dispatchEvent(new CustomEvent('cart-quantity:refresh'));
   */
  document.addEventListener('cart-quantity:refresh', () => {
    cachedCart = null;
    scheduleSynchronization();
  });

  // Product-page quantity controls represent the total quantity of the
  // selected variant in the cart. Update Shopify as soon as they change.
  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-product-quantity-sync]');
    if (!input) return;

    setCartVariantQuantity(input);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true,
    });
  } else {
    initialize();
  }
})();
